import './drop.css';

// Level-up artwork. These belong to the modal's image set rather than the game's own sprite
// folder, so they are imported explicitly instead of coming through require.context below.
import lvlMicro from '../../images/game-modal/micro.svg';
import lvlDjset from '../../images/game-modal/lvl-2-djset.svg';
import lvlMicroMulti from '../../images/game-modal/lvl-3-micro-multi.webp';

// The engine ships as a global (window.DropGame) loaded via a <script> tag — not as an
// ES module — mirroring how sephora-2026 integrates the same build and how the standalone
// package (../sephora-games/standalone/drop) wires it.
const SCRIPT_URL = '/build/games/drop/drop.min.js';

// ── Image assets ──────────────────────────────────────────────────────────────
// Imported through webpack so they get hashed/published. require.context keeps the
// good/bad/special mapping declarative: drop a sprite into the matching folder and it is
// picked up — no list to maintain here.
const uiCtx      = require.context('./images', false, /\.(webp|svg|png)$/);
const goodCtx    = require.context('./images/sprites/good', false, /\.(webp|svg|png)$/);
const badCtx     = require.context('./images/sprites/bad', false, /\.(webp|svg|png)$/);
const specialCtx = require.context('./images/sprites/special', false, /\.(webp|svg|png)$/);

const resolveAsset = (mod) => (mod && mod.default) ? mod.default : mod;
const assetName = (key) => key.replace('./', '').replace(/\.(webp|svg|png)$/, '');
const listAssets = (ctx) => ctx.keys().sort().map((key) => resolveAsset(ctx(key)));
const listNames = (ctx) => ctx.keys().sort().map(assetName);

const ui = {};
uiCtx.keys().forEach((key) => {
    ui[assetName(key)] = resolveAsset(uiCtx(key));
});

const GOOD_SPRITES = listAssets(goodCtx);      // owoce — punktowane
const BAD_SPRITES  = listAssets(badCtx);       // kapsel — kosztuje życie
// The engine takes a single special sprite; the folder holds exactly one (chmiel).
const SPECIAL_SPRITE = listAssets(specialCtx)[0] ?? null;

// ── Per-sprite sizing ─────────────────────────────────────────────────────────
// The engine has no per-sprite size option: every falling item is fitted into the same
// square box (_itemRadius * 2 ≈ 45 × the responsive scale) by aspect ratio. These factors
// scale that box for individual sprites — keyed by file name without extension, 1 = engine
// default, 0.8 = 20% smaller. Unlisted sprites keep the default.
//
// Note this only changes what is DRAWN: the catch test still uses the engine's uniform
// _itemRadius, so a heavily shrunk sprite gets caught slightly outside its visible bounds.
const SPRITE_SCALES = {
    // 'good-1': 0.85,
    // 'kapsel': 1.1,
};

// Base size for every falling item. The engine hardcodes it as half of _baseBucketW
// (Math.round(90 * scale), shared with the bucket) with no config knob, so this factor is
// applied by redefining _itemRadius on the instance — which keeps the catch band, the spawn
// margins and the drawn size in sync. Per-sprite factors above multiply on top of it.
const BASE_ITEM_SCALE = .8;

const scaleFor = (name) => SPRITE_SCALES[name] ?? 1;
const GOOD_SPRITE_SCALES = listNames(goodCtx).map(scaleFor);
const BAD_SPRITE_SCALES  = listNames(badCtx).map(scaleFor);
const SPECIAL_SPRITE_SCALE = scaleFor(listNames(specialCtx)[0] ?? '');

/**
 * Which scale applies to the p5.Image the engine is about to draw. The loaded sprite arrays
 * keep the order of the URLs we handed in (goodSprites/badSprites), so identity in those
 * arrays maps straight back to the folder listing above.
 */
function spriteScale(game, sprite) {
    const assets = game._assets ?? {};
    const good = assets.goodSprites ?? [];
    const bad  = assets.badSprites ?? [];

    const g = good.indexOf(sprite);
    if (g !== -1) {
        return GOOD_SPRITE_SCALES[g] ?? 1;
    }
    const b = bad.indexOf(sprite);
    if (b !== -1) {
        return BAD_SPRITE_SCALES[b] ?? 1;
    }
    if (sprite && sprite === assets.specialSprite) {
        return SPECIAL_SPRITE_SCALE;
    }
    return 1;
}

/**
 * Item sizing overrides, applied on the instance because the engine exposes neither in its
 * config:
 *  - _itemRadius — the base size of every item (BASE_ITEM_SCALE). A getter on the prototype,
 *    so it has to be redefined rather than assigned.
 *  - _spriteSize — the per-sprite box (SPRITE_SCALES). The engine calls it for falling items
 *    and for the instruction pages' sprite rows; the latter are disabled in this build
 *    (texts.startPages is empty), so in practice only gameplay is affected.
 */
function applyItemSizing(game) {
    const proto = Object.getPrototypeOf(game);

    const radius = Object.getOwnPropertyDescriptor(proto ?? {}, '_itemRadius');
    if (BASE_ITEM_SCALE !== 1 && radius?.get) {
        Object.defineProperty(game, '_itemRadius', {
            configurable: true,
            get: () => radius.get.call(game) * BASE_ITEM_SCALE,
        });
    } else if (BASE_ITEM_SCALE !== 1) {
        console.warn('DropGame: _itemRadius not found, base item sizing skipped');
    }

    const original = proto?._spriteSize;
    if (typeof original !== 'function') {
        console.warn('DropGame: _spriteSize not found, per-sprite sizing skipped');
        return;
    }
    game._spriteSize = function (sprite, boxSize) {
        return original.call(this, sprite, boxSize * spriteScale(this, sprite));
    };
}

// Scoring mirrors the ninja-cut era so the on-site copy and prize thresholds stay valid:
// a fruit is worth 10 pts, a chmiel cone 50. specialEffect 'points' (rather than 'life')
// keeps that parity — and it also lets the server treat any lives increase as a violation,
// see DropIntegrityService.
const ITEM_POINTS    = 10;
const SPECIAL_POINTS = 50;
const MAX_LIVES      = 3;

// Level ladder — thresholds/intervals/speeds mirror the engine's shipped defaults and MUST
// stay in sync with DropIntegrityService::LEVELS_FALLBACK (the server replays score-rate and
// threshold checks against them). `specialLimit` caps chmiel spawns per level — note the key:
// the engine reads `specialLimit`, the standalone config.game.js still calls it `oscarLimit`,
// which silently disables specials.
//
// Level-up screens. The engine renders `sub` as the modal header (its argument order in
// _transitionToLevelUp) and `imageUrl` as the artwork; the body slot carries the
// "Level x/10" counter from texts.levelCounter. \n breaks the line — the header is
// white-space: pre-line, so no markup is involved (textContent, not innerHTML).
const LEVEL_SCREENS = [
    {text: 'Impreza zaliczona\nAtmosfera staje się gorąca!', image: lvlMicro},
    {text: 'Zajawka na kolejną imprezę!', image: lvlDjset},
    {text: 'Tłum szaleje\ni czeka na kolejną odsłonę!', image: lvlMicroMulti},
    {text: 'Ta impreza\nchyba się nie skończy!', image: lvlMicroMulti},
];

const LEVELS = [
    {threshold: 100,      spawnInterval: 1120, speedMultiplier: 1.30, specialLimit: 2},
    {threshold: 220,      spawnInterval: 1010, speedMultiplier: 1.46, specialLimit: 1},
    {threshold: 350,      spawnInterval: 910,  speedMultiplier: 1.62, specialLimit: 1},
    {threshold: 490,      spawnInterval: 810,  speedMultiplier: 1.82, specialLimit: 1},
    {threshold: 640,      spawnInterval: 720,  speedMultiplier: 2.02, specialLimit: 1},
    {threshold: 810,      spawnInterval: 640,  speedMultiplier: 2.24, specialLimit: 1},
    {threshold: 990,      spawnInterval: 560,  speedMultiplier: 2.47, specialLimit: 1},
    {threshold: 1180,     spawnInterval: 490,  speedMultiplier: 2.73, specialLimit: 1},
    {threshold: 1380,     spawnInterval: 430,  speedMultiplier: 3.02, specialLimit: 1},
    {threshold: 1590,     spawnInterval: 370,  speedMultiplier: 3.33, specialLimit: 1},
    {threshold: Infinity, spawnInterval: 320,  speedMultiplier: 3.64, specialLimit: Infinity},
];

// Index 0 is the base level (entered without a screen); 1..10 each show one. Cycling through
// LEVEL_SCREENS reuses the four designs across ten level-ups and, because the cycle is longer
// than one, never repeats a text on consecutive levels.
LEVELS.forEach((level, i) => {
    if (i === 0) {
        return;
    }
    const screen = LEVEL_SCREENS[(i - 1) % LEVEL_SCREENS.length];
    level.sub = screen.text;
    level.imageUrl = screen.image;
});

const GAME_CONFIG = {
    maxLives: MAX_LIVES,
    itemPoints: ITEM_POINTS,
    specialPoints: SPECIAL_POINTS,
    specialEffect: 'points',
    heartbeatInterval: 10000,

    baseItemSpeed: 3,
    // The surrounding modal already ran its own asset-preload bar, so the engine's second
    // bar only has to cover sprite decode — keep it short.
    loadingDuration: 1200,
    catcherWidthRatio: 0.28,
    referenceWidth: 550,
    referenceHeight: 700,
    specialSpawnChance: 0.12,
    // Bucket sits flush with the bottom of the play area, and catches from its very top rim.
    catcherBottomRatio: 1,
    catchZoneOffset: 0,
    // The engine's own "koniec gry" modal (the oof screen) is off: this modal has its own
    // result / result-bad screens, so showing both meant two end screens back to back. With it
    // off the engine emits game:end itself once /result answers — see startDrop's onGameEnd.
    showEndModal: false,

    levels: LEVELS,
};

// Canvas colours are RGB triplets (p.fill takes components, not hex), so the brand pair is named
// here rather than repeated at each use.
const LECH_GREEN = [15, 130, 30];  // #0f821e — $primary
const LECH_RED   = [228, 23, 19];  // #e41713 — $secondary

// Branding / assets config. Colours: `primary`/`secondary` are RGB triplets consumed by the
// canvas (p.fill), the rest are CSS custom properties the engine writes onto the container.
function buildAssetsConfig(demo) {
    return {
        // Adds `drop-theme--lech` on the container: flat black HUD bar and an inline black/red
        // level-up block instead of the engine's rounded dialog (see the engine's drop.css).
        theme: 'lech',

        // The level-up artwork hangs off the modal root, not the card — .drop-modal__box is
        // position:relative, so an image inside it can only ever anchor to the card. From the root
        // it pins to the bottom of the play area, matching micro.svg on the other screens.
        modalImagePlacement: 'root',

        // Mirrors the modal's own HUD: lives on the left, score on the right.
        hud: [
            {type: 'lives'},
            {type: 'score', label: demo ? 'Próba:' : 'Punkty:'},
        ],

        texts: {
            tapToContinue: 'Dotknij aby kontynuować',
            continueButton: 'Dalej',
            levelUpButton: 'Graj dalej',
            // Intro pages are disabled in this build (the engine boots straight into play) —
            // the modal owns the instruction screens, see templates/modals/application.html.twig.
            startPages: [],
            scoreLabel: demo ? 'PRÓBA' : 'PKT',
            // Second, smaller line on the level-up screen. {total} = number of level-ups.
            levelCounter: 'Poziom {level}/{total}',
            endPositiveTitle: 'Koniec gry',
            endPositiveScore: demo
                ? 'Próba zakończona. Twój wynik: {score} pkt — ten wynik się nie liczy.'
                : 'Zdobyłeś {score} pkt.',
            endNegativeBody: 'Koniec gry!',
            endLoadingText: 'Ładowanie',
            // Ranking screens are disabled in this build; the texts stay for engine parity.
            rankingTitle: '',
            rankingColCast: '',
            rankingColScore: '',
            rankingButtonAdd: '',
            rankingButtonSave: '',
            rankingButtonNext: '',
        },
        images: {
            background: ui['background'] ?? null,
            bucket: ui['bucket'] ?? null,
            specialSprite: SPECIAL_SPRITE,
            oof: ui['oof'] ?? null,
            levelUp: ui['lvl-up'] ?? null,
            lifeIndicator: ui['heart-full'] ?? null,
            lostLifeIndicator: ui['heart-empty'] ?? null,
            goodSprites: GOOD_SPRITES,
            badSprites: BAD_SPRITES,
        },
        sounds: {},
        colors: {
            primary: LECH_GREEN,       // #0f821e — Lech green ($primary)
            secondary: LECH_RED,       // #e41713 — Lech red ($secondary)
            brandRed: '#e41713',
            surface: '#ffffff',
            dark: '#111111',
            btnGradient: 'linear-gradient(160deg, #0f821e 0%, #0c5715 100%)',
            // Loading bar (canvas-drawn, so not reachable from SCSS): white track, green fill,
            // black frame.
            loaderTrack:  [255, 255, 255],
            loaderFill:   LECH_GREEN,
            loaderBorder: [0, 0, 0],
        },
    };
}

function apiBase(uuid) {
    return `/api/application/${uuid}/drop`;
}

/**
 * Preload every game image so the canvas has them ready before the first frame.
 * `onProgress` is called with a 0..1 fraction as each image settles (load or error),
 * driving the loading screen's 10 life-indicator progress bar. Resolves once
 * everything settled.
 */
export function preloadDropAssets(onProgress = () => {
}) {
    const urls = [
        ...Object.values(ui),
        ...GOOD_SPRITES,
        ...BAD_SPRITES,
        ...(SPECIAL_SPRITE ? [SPECIAL_SPRITE] : []),
    ].filter(Boolean);
    const total = urls.length || 1;
    let settled = 0;

    const settle = () => {
        settled += 1;
        onProgress(Math.min(1, settled / total));
    };

    const loadOne = (url) => new Promise((res) => {
        const img = new Image();
        img.onload = () => {
            settle();
            res();
        };
        img.onerror = () => {
            settle();
            res();
        };
        img.src = url;
    });

    return Promise.all(urls.map(loadOne)).then(() => onProgress(1));
}

let gameInstance = null;

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (window.DropGame || document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

/**
 * Mount and run the game inside `container`.
 *  - demo === true  → no score registration, no heartbeats (mock API, PRÓBA HUD).
 *  - demo === false → registers the score + sends heartbeats to the backend.
 *
 * Drop posts no per-catch events — the engine's apiClient handles start / heartbeat /
 * result, so (unlike sepho-race) there are no gameplay events to forward.
 *
 * There is no timer: a run ends when the last life is lost (or on an explicit forceEnd).
 * The engine's end modal is disabled (GAME_CONFIG.showEndModal), so it emits game:end as soon
 * as /result answers and `onDone(score)` hands straight over to this modal's result screen —
 * no user gesture in between, and no two end screens stacked on each other.
 */
export function startDrop(container, {
    demo = false, uuid = null, onDone = () => {
    }, onError = null
} = {}) {
    teardownDrop();
    container.classList.add('drop-game');

    const apiConfig = demo
        // Demo: mock API with no sessionToken → engine heartbeats stay disabled.
        ? {useMock: true, mockDelay: 0, mockResponses: {startGame: {}, submitResult: {}}}
        : {
            useMock: false,
            endpoints: {
                startGame: `${apiBase(uuid)}/start`,
                heartbeat: `${apiBase(uuid)}/heartbeat`,
                submitResult: `${apiBase(uuid)}/result`,
            },
        };

    // The engine reports the final score on game:complete (once /result answered) and then
    // immediately emits game:end. Remember the score from the first so the second can report
    // it — game:end itself carries no payload.
    let finalScore = null;

    loadScript(SCRIPT_URL)
        .then(() => {
            if (!window.DropGame) {
                console.error('DropGame: script loaded but global is not defined');
                onDone(0);
                return;
            }

            gameInstance = window.DropGame.init(container.id, {
                assetsConfig: buildAssetsConfig(demo),
                gameConfig: GAME_CONFIG,
                apiConfig,
                callbacks: {
                    onComplete: (result) => {
                        finalScore = result?.score ?? null;
                    },
                    onGameEnd: () => {
                        const score = finalScore ?? gameInstance?._score ?? 0;
                        teardownDrop();
                        onDone(score);
                    },
                    onError: (error) => {
                        console.error('DropGame error:', error);
                        // A failed /start (e.g. already played, start window over) surfaces here as
                        // an ApiError carrying { status, data }. The engine swallows it and boots
                        // into play regardless, so tear the instance down before handing over.
                        teardownDrop();
                        if (onError) {
                            onError(error);
                        } else {
                            onDone(0);
                        }
                    },
                },
            });

            if (gameInstance) {
                applyItemSizing(gameInstance);
            }
        })
        .catch((e) => {
            console.error('DropGame: failed to load game script', e);
            onDone(0);
        });
}

export function teardownDrop() {
    if (gameInstance) {
        try {
            gameInstance.destroy();
        } catch { /* ignore */
        }
        gameInstance = null;
    }
}

/** Pause a running game (no-op unless the engine is mid-play). */
export function pauseDrop() {
    try {
        gameInstance?.pause?.();
    } catch { /* ignore */
    }
}

/** Resume a paused game (no-op unless the engine is paused). */
export function resumeDrop() {
    try {
        gameInstance?.resume?.();
    } catch { /* ignore */
    }
}
